import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';

import { ActionButton } from 'components/ui/Button';
import DatePicker from 'components/ui/DatePicker/DatePicker';
import Label from 'components/ui/Label';

import { isEmpty, isValidAlphanumericName, isValidEmail } from 'utils/validators';

import { ErrorField, Field, MandatoryFieldLabel, StyledForm, StyledInput, StyledRow, StyledSubmitField } from './SearchBooking.styles';

const SearchByOtherDetailsForm = ({ intl, onSubmitSearch }) => {
  const [departureDateValue, setDepartureDateValue] = useState(null);
  const [departureDateError, setDepartureDateError] = useState('');
  const [travelEndDateValue, setTravelEndDateValue] = useState(null);
  const [travelEndDateError, setTravelEndDateError] = useState('');

  const { register, handleSubmit, watch, setValue, formState } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      lastName: '',
      departureDate: null,
      travelEndDate: null,
      email: '',
      serviceName: ''
    }
  });

  const { errors } = formState;

  const serviceName = watch('serviceName');
  const lastName = watch('lastName');
  const email = watch('email');

  const hasErrors = Boolean(Object.keys(errors).length > 0);

  const setDepartureDate = (_, value) => {
    setDepartureDateValue(value);
    setTravelEndDateValue(value);
  };

  const clearDepartureDate = () => {
    setDepartureDateValue(null);
  };

  const setEndDate = (_, value) => {
    setTravelEndDateValue(value);
  };

  const clearEndDate = () => {
    setTravelEndDateValue(null);
  };

  const trainNumberHandlers = {
    onKeyDown: (event) => {
      if (event?.keyCode === 69) {
        event?.preventDefault();
      }
    },
    onInput: ({ target }) => {
      if (target?.value?.length > 10) {
        target.value = target.value.slice(0, 10);
      }
    }
  };

  const shouldDisableServiceNameField =
    Boolean(departureDateValue) && Boolean(travelEndDateValue) && !departureDateValue.isSame(travelEndDateValue);

  useEffect(() => {
    if (shouldDisableServiceNameField && serviceName) {
      setValue('serviceName', '');
    }
  }, [serviceName, setValue, shouldDisableServiceNameField]);

  return (
    <StyledForm
      data-testid="per-train-form"
      onSubmit={handleSubmit((props) => onSubmitSearch({ ...props, departureDate: departureDateValue, travelEndDate: travelEndDateValue }))}
    >
      <Field>
        <MandatoryFieldLabel htmlFor="lastName">
          <FormattedMessage id="searchBooking.lastName" />
        </MandatoryFieldLabel>
        <StyledInput
          {...register('lastName', { required: false, validate: (value) => isEmpty(value) || isValidAlphanumericName(value) })}
          id="lastName"
          type="text"
          data-cy="lastName-input"
          data-testid="lastName-input"
        />
        {errors.lastName && (
          <ErrorField id="error-lastName" data-testid="error-lastName">
            <FormattedMessage id="searchBooking.lettersValidationMessage" />
          </ErrorField>
        )}
      </Field>
      <StyledRow>
        <Field>
          <MandatoryFieldLabel htmlFor="departureDate">
            <FormattedMessage id="searchBooking.travelDateFrom" />
          </MandatoryFieldLabel>
          <DatePicker
            id="departureDate"
            name="departureDate"
            clearTravelDate={clearDepartureDate}
            setTravelDate={setDepartureDate}
            intl={intl}
            value={departureDateValue}
            errorMessage={departureDateError}
            setErrorMessage={setDepartureDateError}
          />
        </Field>
        <Field>
          <MandatoryFieldLabel htmlFor="travelEndDate">
            <FormattedMessage id="searchBooking.travelDateTo" />
          </MandatoryFieldLabel>
          <DatePicker
            id="travelEndDate"
            name="travelEndDate"
            clearTravelDate={clearEndDate}
            setTravelDate={setEndDate}
            intl={intl}
            value={travelEndDateValue}
            minDate={departureDateValue}
            maxDate={departureDateValue ? moment(departureDateValue).add(30, 'days') : null}
            errorMessage={travelEndDateError}
            setErrorMessage={setTravelEndDateError}
          />
        </Field>
      </StyledRow>
      <Field>
        <Label htmlFor="serviceName">
          <FormattedMessage id="searchBooking.serviceName" />
        </Label>
        <StyledInput
          {...register('serviceName', { required: false })}
          id="serviceName"
          type="number"
          data-cy="serviceName-input"
          data-testid="serviceName-input"
          disabled={shouldDisableServiceNameField}
          {...trainNumberHandlers}
        />
        {errors.serviceName && (
          <ErrorField id="error-serviceName" data-testid="error-serviceName">
            <FormattedMessage id="searchBooking.trainNumberValidationMessage" />
          </ErrorField>
        )}
      </Field>
      <Field>
        <MandatoryFieldLabel htmlFor="email">
          <FormattedMessage id="searchBooking.email" />
        </MandatoryFieldLabel>
        <StyledInput
          {...register('email', { required: false, validate: (value) => isEmpty(value) || isValidEmail(value) })}
          id="email"
          type="text"
          data-cy="email-input"
          data-testid="email-input"
        />
        {errors.email && (
          <ErrorField id="error-email" data-testid="error-email">
            <FormattedMessage id="searchBooking.emailValidationMessage" />
          </ErrorField>
        )}
      </Field>
      <StyledSubmitField>
        <ActionButton
          data-cy="search-by-details"
          data-testid="search-by-details"
          type="submit"
          disabled={!departureDateValue || !travelEndDateValue || (!lastName && !email) || hasErrors}
        >
          <FormattedMessage id="searchBooking.search" />
        </ActionButton>
      </StyledSubmitField>
    </StyledForm>
  );
};

SearchByOtherDetailsForm.propTypes = {
  onSubmitSearch: PropTypes.func,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func
  })
};

export default SearchByOtherDetailsForm;
